import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useWorkspace, useDeviceLayout } from '@/hooks';
import { apiClient } from '@/services/api';
import { API_ENDPOINTS } from '@/constants/api';
import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout';
import { MobilePageLayout } from '@/components/layout/MobilePageLayout';
import { WorkspaceCreationForm } from '../WorkspaceCreationForm';
import type { WorkspaceCreationFormData } from '../WorkspaceCreationForm';
import type { InvitationRequestDto } from '@/types/api/requests';
import type { SendBatchInvitationsResponse } from '@/types/api/responses';
import { BrandLogo } from '@/components/common/BrandLogo';
import { OnboardingMapIllustration } from './components/OnboardingMapIllustration';
import { OnboardingSettingsBar } from '@/components/layout/OnboardingSettingsBar';
import { SuccessContent } from './components/SuccessContent';
import { LeftPanelContent } from './components/LeftPanelContent';
import { RightPanelContent } from './components/RightPanelContent';
import styles from './WorkspaceOnboardingPage.module.css';

export const WorkspaceOnboardingPage = memo(function WorkspaceOnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { createWorkspace, isCreating, currentWorkspace } = useWorkspace();
  const [showCTA, setShowCTA] = useState(false);
  const [createdWorkspaceName, setCreatedWorkspaceName] = useState('');
  const { isMobile } = useDeviceLayout();

  const handleSubmit = useCallback(
    async (data: WorkspaceCreationFormData) => {
      const result = await createWorkspace({
        name: data.name,
        description: data.description,
      });

      if (result.success) {
        if (data.invitations.length > 0) {
          const invitationRequests: InvitationRequestDto[] =
            data.invitations.map((invitation) => ({
              email: invitation.email,
              role: invitation.role,
            }));

          try {
            await apiClient.post<SendBatchInvitationsResponse>(
              API_ENDPOINTS.WORKSPACES.INVITATIONS(result.workspaceId),
              { invitations: invitationRequests }
            );
          } catch (error) {
            console.error('Failed to send invitations:', error);
          }
        }

        setCreatedWorkspaceName(data.name);
        setShowCTA(true);
      }
    },
    [createWorkspace]
  );

  const handleGoToWorkspace = useCallback(() => {
    const shortId = currentWorkspace?.shortId;
    if (shortId) {
      navigate(`/w/${shortId}/map/tasks`);
    }
  }, [navigate, currentWorkspace]);

  const handleGoToDashboard = useCallback(() => {
    const shortId = currentWorkspace?.shortId;
    if (shortId) {
      navigate(`/w/${shortId}/dashboard`);
    }
  }, [navigate, currentWorkspace]);

  /* ── Mobile layout ────── */
  if (isMobile) {
    return (
      <MobilePageLayout
        logo={<BrandLogo size="sm" linkToHome />}
        actions={<OnboardingSettingsBar />}
      >
        {showCTA ? (
          <SuccessContent
            workspaceName={createdWorkspaceName}
            onGoToWorkspace={handleGoToWorkspace}
            onGoToDashboard={handleGoToDashboard}
            isMobile
          />
        ) : (
          <>
            <h1 className={styles.mobileHeading}>
              {t('workspace.onboarding.headingPrefix')}{' '}
              <span className={styles.brandAccent}>GeoMarkup</span>!
            </h1>
            <p className={styles.mobileSubtitle}>
              {t('workspace.onboarding.form.subtitle')}
            </p>
            <WorkspaceCreationForm
              onSubmit={handleSubmit}
              isSubmitting={isCreating}
              className={styles.mobileForm}
            />
          </>
        )}
      </MobilePageLayout>
    );
  }

  /* ── Desktop layout ────── */
  return (
    <SplitScreenLayout
      leftPanelBackground={<OnboardingMapIllustration />}
      leftPanel={<LeftPanelContent />}
      rightPanel={
        <RightPanelContent
          showCTA={showCTA}
          createdWorkspaceName={createdWorkspaceName}
          onGoToWorkspace={handleGoToWorkspace}
          onGoToDashboard={handleGoToDashboard}
          onSubmit={handleSubmit}
          isCreating={isCreating}
        />
      }
      settingsBar={<OnboardingSettingsBar />}
    />
  );
});
